import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EditRecipeForm from '../components/recipes/EditRecipeForm';
import CommentList from '../components/comments/CommentList';
import { useAuth } from '../context/AuthContext'; // Import useAuth
import FullScreenRecipeView from '../components/recipes/FullScreenRecipeView';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import RecipeRealizationsSection from '../components/realizations/RecipeRealizationsSection';
import EditRecipeReadView from '../components/recipes/EditRecipeReadView';
import { Page, PageStatus } from '../components/ui';
import useRecipeDetails from '../hooks/recipes/useRecipeDetails';
import useRecipeViewState from '../hooks/recipes/useRecipeViewState';
import useStickyRecipeHeader from '../hooks/recipes/useStickyRecipeHeader';
import useEditRecipePageController from '../hooks/recipes/useEditRecipePageController';

const EditRecipePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { recipe, loading, error, reloadRecipe } = useRecipeDetails(id);
  const {
    isEditing,
    isCustomFullScreen,
    isImageOverlayOpen,
    openFullScreen,
    closeFullScreen,
    openImageOverlay,
    closeImageOverlay,
    toggleEdit,
    setEditing,
  } = useRecipeViewState();
  const imageWrapperRef = useRef<HTMLDivElement | null>(null);
  const tapedPhotoEffectRef = useRef<HTMLDivElement | null>(null);
  const stickyImageButtonRef = useRef<HTMLButtonElement | null>(null);
  const {
    isStickyRecipeHeaderVisible,
    stickyHeaderProgress,
    sharedPhotoStyle,
    isSharedPhotoVisible,
  } = useStickyRecipeHeader({
    imageWrapperRef,
    tapedPhotoEffectRef,
    stickyImageButtonRef,
    disabled: isEditing || isCustomFullScreen,
    recipeId: recipe?.id,
  });
  
  const { author } = useAuth(); // Get current authenticated author

  const { addFavorite, removeFavorite, isFavorite, favoritePendingIds } = useFavorites();
  const { showToast } = useToast();
  const {
    isAuthor,
    isRecipeFavorite,
    handleToggleEdit,
    handleShare,
    handleRecipeUpdatedInForm,
    handleRecipeDeletedInForm,
    handleFavoriteToggle,
  } = useEditRecipePageController({
    recipe,
    recipeId: id,
    author,
    reloadRecipe,
    navigate,
    setEditing,
    toggleEdit,
    isFavorite,
    addFavorite,
    removeFavorite,
    showToast,
  });

  if (loading) {
    return <PageStatus marginTop={4} centered>Chargement de la recette...</PageStatus>;
  }

  if (error) {
    return <PageStatus marginTop={4} alertVariant="danger">{error}</PageStatus>;
  }

  if (!recipe) {
    return <PageStatus marginTop={4} alertVariant="warning">Recette introuvable.</PageStatus>;
  }

  if (isCustomFullScreen) {
    return <FullScreenRecipeView recipe={recipe} onClose={closeFullScreen} />;
  }

  return (
    <Page fluid marginBottom={5} className="no-print full-width-container-fluid">
      {!isEditing ? (
        <EditRecipeReadView
          recipe={recipe}
          isAuthor={isAuthor}
          isRecipeFavorite={isRecipeFavorite}
          favoritePendingIds={favoritePendingIds}
          isStickyRecipeHeaderVisible={isStickyRecipeHeaderVisible}
          stickyHeaderProgress={stickyHeaderProgress}
          isSharedPhotoVisible={isSharedPhotoVisible}
          sharedPhotoStyle={sharedPhotoStyle}
          isImageOverlayOpen={isImageOverlayOpen}
          imageWrapperRef={imageWrapperRef}
          tapedPhotoEffectRef={tapedPhotoEffectRef}
          stickyImageButtonRef={stickyImageButtonRef}
          onOpenImageOverlay={openImageOverlay}
          onCloseImageOverlay={closeImageOverlay}
          onFavoriteToggle={() => { void handleFavoriteToggle(); }}
          onShare={() => { void handleShare(); }}
          onOpenFullScreen={openFullScreen}
          onToggleEdit={handleToggleEdit}
        />
      ) : (
        // Edit mode
        <EditRecipeForm
          recipe={recipe}
          onRecipeUpdated={handleRecipeUpdatedInForm} // Use new handler
          onRecipeDeleted={handleRecipeDeletedInForm} // Use new handler
          onCancel={handleToggleEdit}
        />
      )}
      <RecipeRealizationsSection
        recipeId={recipe.id}
        recipeAuthorId={recipe.author_id}
        className="no-print"
      />
      <CommentList recipeId={recipe.id} className="no-print" /> {/* CommentList should also be no-print if it's not the desired content */}
    </Page>
  );
};

export default EditRecipePage;
